import {css, Button, Link} from '@sail/ui/deprecated';
import {view, Icon} from '@sail/ui';
import {
  InlineDatumReactComponentProps,
  useAnalytics,
} from '@stripe-internal/inline-data';
import {
  Card,
  useTheme,
  ThemeProvider,
  LinkTheme,
} from '@stripe-internal/conversations-ui';
import {safeWindowOpen} from '@stripe-internal/safe-links';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';
import {business} from '@sail/icons/react/Icon';

export type ValueProps = {
  merchant_name: string;
  cta_link: string;
  cta_copy: string;
  merchant_avatar: string;
  purchase_amount: string;
  purchase_date: number;
};

const messages = defineMessages({
  purchaseDate: {
    id: 'mse_inline_data.universal_help_purchase_detail.purchased_on',
    description: 'Message displaying the date of a purchase.',
    defaultMessage: 'Purchased on {date}',
  },
  viewPurchasesCTA: {
    id: 'mse_inline_data.universal_help_purchase_detail.view_all_purchases_cta',
    description: 'Call to action for button linking to all Link purchases.',
    defaultMessage: 'View all purchases',
  },
  recentPurchaseInstructions: {
    id: 'mse_inline_data.universal_help_purchase_detail.recent_purchase_instructions',
    description: 'Message before showing the purchase detail.',
    defaultMessage: 'Your most recent purchase from Link was:',
  },
  noPurchaseInstructions: {
    id: 'mse_inline_data.universal_help_purchase_detail.no_purchase_instructions',
    description:
      'Message directing user to Link Dashboard for future purchases.',
    defaultMessage:
      'There are no purchases available on your account at this time. When you make a purchase with Link in the future, you will be able to find it in your <link_to_dashboard>Link Dashboard</link_to_dashboard>.',
  },
});

const LinkRecentPurchaseDetail = ({
  props,
}: {
  props: InlineDatumReactComponentProps;
}) => {
  const {datum} = props;
  const value = datum.value as ValueProps;
  const intl = useIntl();

  const {
    merchant_name: merchantName,
    purchase_amount: purchaseAmount,
    purchase_date: purchaseDate,
    merchant_avatar: merchantAvatar,
  } = value;

  const localizedPurchaseDate =
    purchaseDate &&
    intl.formatDate(new Date(purchaseDate * 1000), {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  const hasPurchaseHistory = merchantName && purchaseAmount;

  const theme = useTheme();

  const analytics = useAnalytics();

  analytics.viewed('recent_purchase_detail', {
    hasPurchaseHistory,
  });

  if (!hasPurchaseHistory) {
    return (
      <view.div
        data-testid="no-purchase-instruction"
        css={{font: 'label.medium'}}
      >
        <FormattedMessage
          {...messages.noPurchaseInstructions}
          values={{
            link_to_dashboard: (chunks) => (
              <Link
                onPress={() => safeWindowOpen('http://app.link.com/purchases')}
                css={{
                  color: css.value(theme.linkColor),
                  underline: css.value('none'),
                }}
              >
                {chunks}
              </Link>
            ),
          }}
        />
      </view.div>
    );
  }

  return (
    <view.div css={{gap: 'medium', stack: 'y'}}>
      <view.div data-testid="contact-instructions">
        <FormattedMessage {...messages.recentPurchaseInstructions} />
      </view.div>
      <Card>
        <view.div css={{stack: 'y', gap: 'large'}}>
          <view.div css={{stack: 'x', gap: 'small'}}>
            <view.div
              css={{
                stack: 'x',
                width: 'large',
                height: 'large',
                alignSelfY: 'center',
              }}
            >
              {merchantAvatar ? (
                <view.img
                  data-testid="merchant-avatar"
                  src={merchantAvatar}
                  css={{
                    width: 'fill',
                    height: 'fill',
                    borderRadius: '50%',
                    border: '1px inset rgba(0, 0, 0, 0.15)',
                  }}
                />
              ) : (
                <view.div
                  css={{
                    borderRadius: '50%',
                    border: '1px inset rgba(0, 0, 0, 0.15)',
                    width: 'fill',
                    height: 'fill',
                  }}
                >
                  <Icon
                    icon={business}
                    css={{
                      padding: 'xsmall',
                      width: 'fill',
                      height: 'fill',
                    }}
                  />
                </view.div>
              )}
            </view.div>

            <view.h3
              css={{margin: 0, font: 'heading.small'}}
              data-testid="merchant-name"
            >
              {merchantName}
            </view.h3>
          </view.div>
          <view.div>
            <view.div
              data-testid="purchase-amount"
              css={{font: 'heading.xlarge'}}
            >
              {purchaseAmount}
            </view.div>
            {purchaseDate ? (
              <view.div
                data-testid="purchase-date"
                css={{font: 'label.small', textColor: 'subdued'}}
              >
                <FormattedMessage
                  {...messages.purchaseDate}
                  values={{date: localizedPurchaseDate}}
                />
              </view.div>
            ) : null}
          </view.div>

          <Button
            data-testid="cta-button"
            type="secondary"
            css={{width: 'fill', size: 'small'}}
            onClick={() => {
              safeWindowOpen('https://app.link.com/purchases');
              analytics.action('recent_purchase_detail_click', {
                viewPurchasesCTA: messages.viewPurchasesCTA,
              });
            }}
          >
            <FormattedMessage {...messages.viewPurchasesCTA} />
          </Button>
        </view.div>
      </Card>
    </view.div>
  );
};

export const RecentPurchaseDetail = (props: InlineDatumReactComponentProps) => {
  return (
    <ThemeProvider value={LinkTheme}>
      <LinkRecentPurchaseDetail props={props} />
    </ThemeProvider>
  );
};
